import { ALLOWED_SPECIAL_CHAR_ADDRESS } from '@/constants';
import React from 'react';

export const blockSensitiveCharacter = (event: any) => {
  // CLS allows: letters, numbers, space, _ - . , ' @ / ( ) &
  const validator = /[`~<>;:"[\]\\|{}=+#$%^*!?]/g;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const blockEmployerCharacter = (event: any) => {
  // CLS allows: letters, numbers, space, _ - . , @ ( ) : ; & ' /
  const validator = /[`~<>"[\]\\|{}=+#$%^*!?]/g;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const emailCharacter = (event: any) => {
  const validator = /[-`~<>;:" [-\]|{}()=+&#$%^*!?]/g;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

const sanitizeAllowed = (s: string) =>
  s.replace(ALLOWED_SPECIAL_CHAR_ADDRESS, '');

const normalizeWhileTyping = (s: string) => {
  // 1) remove disallowed chars
  // 2) collapse multi-spaces to single
  // 3) remove leading spaces (keep trailing single space while typing)
  return sanitizeAllowed(s)
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+/, '');
};

export const addressCharacter = (e: React.FormEvent<HTMLInputElement>) => {
  const input = e.currentTarget;
  if ((input as any)._isComposing) return;

  const original = input.value;
  const cleaned = normalizeWhileTyping(original);

  if (cleaned !== original) {
    const selStart = input.selectionStart ?? cleaned.length;
    const prefix = original.slice(0, selStart);
    const newPos = normalizeWhileTyping(prefix).length;

    input.value = cleaned;
    input.setSelectionRange(newPos, newPos);
  }
};

export const handleAddressBlur = (e: React.FocusEvent<HTMLInputElement>) => {
  const input = e.currentTarget;
  // Final normalize: collapse spaces and trim both ends
  const finalValue = sanitizeAllowed(input.value).replace(/\s+/g, ' ').trim();
  if (finalValue !== input.value) input.value = finalValue;
};

// Optional IME support
export const handleCompositionStart = (
  e: React.CompositionEvent<HTMLInputElement>,
) => {
  (e.currentTarget as any)._isComposing = true;
};
export const handleCompositionEnd = (
  e: React.CompositionEvent<HTMLInputElement>,
) => {
  (e.currentTarget as any)._isComposing = false;
  addressCharacter(e as any);
};

export const alphabetOnly = (event: any) => {
  const validator = /[1234567890`@~<>;:"[-\]|{}(/.,')=_+&#$%^*!?]/g;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const alphaNumericOnly = (event: any) => {
  const validator = /[-`@~<>;:" [-\]|{}(/.,')=_+&#$%^*!?]/g;
  if (validator.test(event.key)) {
    event.preventDefault();
  }
};

export const numericOnly = (event: any) => {
  const numericAllowKeys = ['Backspace', 'Delete', 'Arrow'];
  // const validator = /[a-zA-Z\-`@~<>;:" [-\]|{}(/.,')=_+&#$%^*!?]/g;
  const validator = /[\d.]/g;

  if (
    validator.test(event.key) ||
    numericAllowKeys.find((x) => event.key.includes(x)) !== undefined ||
    event.ctrlKey
  ) {
    return true;
  } else {
    event.preventDefault();
  }
};

export const setDOB = (event: any) => {
  if (!event || !event.match(/^\d{12}$/)) {
    return null;
  }

  const nricYear = parseInt(event.substr(0, 2), 10);
  const nricMonth = parseInt(event.substr(2, 2), 10);
  const nricDay = parseInt(event.substr(4, 2), 10);

  // validate NRIC date components
  if (nricMonth < 1 || nricMonth > 12 || nricDay < 1 || nricDay > 31) {
    return null;
  }
  const currentYear = new Date().getFullYear();
  const currentYearLastTwoDigits = currentYear % 100;
  const currentYearFirstTwoDigits = Math.floor(currentYear / 100);

  // calculate year based on NRIC's first two digits
  const nricYearPrefix =
    currentYearLastTwoDigits < nricYear
      ? currentYearFirstTwoDigits - 1
      : currentYearFirstTwoDigits;
  const fullYear = parseInt(
    nricYearPrefix.toString() +
      (nricYear < 10 ? '0' + nricYear.toString() : nricYear.toString()),
    10,
  );
  // validate and create the Date object
  const dobDate = new Date(fullYear, nricMonth - 1, nricDay);

  // check if the calculated date is valid
  if (
    !(
      dobDate.getFullYear() !== fullYear ||
      dobDate.getMonth() !== nricMonth - 1 ||
      dobDate.getDate() !== nricDay
    )
  ) {
    const formatDateToDDMMYYYY = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    };

    return formatDateToDDMMYYYY(dobDate);
  } else {
    return null;
  }
};

export const setDOBForm = (nricNumber: any) => {
  if (!nricNumber || !nricNumber.match(/^\d{12}$/)) {
    return null;
  }

  const nricYear = parseInt(nricNumber.substr(0, 2), 10);
  const nricMonth = parseInt(nricNumber.substr(2, 2), 10);
  const nricDay = parseInt(nricNumber.substr(4, 2), 10);

  // validate NRIC date components
  if (nricMonth < 1 || nricMonth > 12 || nricDay < 1 || nricDay > 31) {
    return null;
  }
  const currentYear = new Date().getFullYear();
  const currentYearLastTwoDigits = currentYear % 100;
  const currentYearFirstTwoDigits = Math.floor(currentYear / 100);

  // calculate year based on NRIC's first two digits
  const nricYearPrefix =
    currentYearLastTwoDigits < nricYear
      ? currentYearFirstTwoDigits - 1
      : currentYearFirstTwoDigits;
  const fullYear = parseInt(
    nricYearPrefix.toString() +
      (nricYear < 10 ? '0' + nricYear.toString() : nricYear.toString()),
    10,
  );
  // validate and create the Date object
  const dobDate = new Date(fullYear, nricMonth - 1, nricDay);

  // check if the calculated date is valid
  if (
    !(
      dobDate.getFullYear() !== fullYear ||
      dobDate.getMonth() !== nricMonth - 1 ||
      dobDate.getDate() !== nricDay
    )
  ) {
    const formatDateToDDMMYYYY = (date) => {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    return formatDateToDDMMYYYY(dobDate);
  } else {
    return null;
  }
};

export const handlePaste = (event: any) => {
  event.preventDefault();
};

export const capitalizeFirstChar = (letter: string) => {
  return letter.charAt(0).toUpperCase() + letter.slice(1);
};

export const capitalizeFirstLetterOfEachWord = (inputText: string) => {
  if (inputText !== undefined) {
    // Split the input text into words
    const words = inputText.split(' ');

    // Capitalize the first letter of each word
    const capitalizedWords = words.map((word) => {
      if (word.length > 0) {
        const wordToLower = word.toLowerCase();
        return wordToLower[0].toUpperCase() + wordToLower.slice(1);
      }
      return word; // Handle empty words if necessary
    });

    // Join the capitalized words back into a string
    const capitalizedText = capitalizedWords.join(' ');

    return capitalizedText;
  }
};
